import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { clientAPI, reportAPI } from "../lib/api";
import { formatCurrency, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, ledgerRes] = await Promise.all([
        clientAPI.get(id),
        reportAPI.clientLedger(id),
      ]);
      setClient(clientRes.data);
      setLedger(ledgerRes.data);
    } catch (error) {
      toast.error("Failed to load client details");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/clients")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Client Info */}
        <div className="lg:w-1/3 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Client Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{client.name}</h2>
                {client.gstin && (
                  <p className="font-mono text-sm text-slate-500">GSTIN: {client.gstin}</p>
                )}
              </div>

              {(client.email || client.phone) && (
                <div className="space-y-2">
                  {client.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline">
                        {client.email}
                      </a>
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${client.phone}`} className="text-blue-600 hover:underline">
                        {client.phone}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {(client.address_line1 || client.city) && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div className="text-slate-600">
                    {client.address_line1 && <div>{client.address_line1}</div>}
                    {client.address_line2 && <div>{client.address_line2}</div>}
                    <div>
                      {[client.city, client.state, client.pincode].filter(Boolean).join(", ")}
                    </div>
                  </div>
                </div>
              )}

              {client.contact_person && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-slate-500">Contact Person</p>
                  <p className="font-medium">{client.contact_person}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Account Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Total Invoiced</span>
                <span className="font-mono font-medium">
                  {formatCurrency(ledger?.total_invoiced || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Total Paid</span>
                <span className="font-mono font-medium text-emerald-600">
                  {formatCurrency(ledger?.total_paid || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="font-medium">Outstanding</span>
                <span className={`font-mono font-bold text-lg ${ledger?.outstanding > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                  {formatCurrency(ledger?.outstanding || 0)}
                </span>
              </div>
            </CardContent>
          </Card>

          <Button
            className="w-full"
            onClick={() => navigate(`/invoices/new?client=${id}`)}
            data-testid="create-invoice-for-client"
          >
            <FileText className="w-4 h-4 mr-2" />
            Create Invoice
          </Button>
        </div>

        {/* Ledger */}
        <div className="lg:w-2/3">
          <Card>
            <CardHeader>
              <CardTitle>Client Ledger</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {ledger?.entries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.entries.map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(entry.date)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {entry.reference}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {entry.description}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(entry.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No transactions yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
